import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Minus, Plus, ShoppingCart, Star, Truck, Shield, RefreshCw, Eye, X, ChevronLeft } from 'lucide-react';
import { ROUTE_PATHS, formatPrice } from '@/lib/index';
import { useProducts } from '@/hooks/useSupabaseData';
import { useCart } from '@/hooks/useCart';
import { ProductCard } from '@/components/ProductCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { trackViewContent, trackAddToCart } from '@/lib/analytics';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const { products, loading } = useProducts();
  const [quantity, setQuantity] = useState(1);
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);

  const product = products.find((p) => p.id === id);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-24 text-center">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-48 mx-auto mb-4"></div>
          <div className="h-4 bg-muted rounded w-64 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-24 text-center">
        <h1 className="text-4xl font-bold mb-4">المنتج غير موجود</h1>
        <p className="text-muted-foreground mb-8">المنتج الذي تبحث عنه غير متوفر</p>
        <Button onClick={() => navigate(ROUTE_PATHS.PRODUCTS)}>تصفح المنتجات</Button>
      </div>
    );
  }

  const relatedProducts = products.filter(
    (p) => p.category === product.category && p.id !== product.id
  ).slice(0, 4);

  // Track ViewContent when product loads
  useEffect(() => {
    if (product) {
      trackViewContent(product);
    }
  }, [product]);

  // بناء معرض الصور
  const allImages = [
    product.image,
    ...(product.images || []),
    ...(product.colors?.map(c => c.image) || [])
  ].filter((img, index, self) => img && self.indexOf(img) === index); // إزالة التكرار

  const currentImage = allImages[selectedImageIndex] || product.image;

  const handleAddToCart = () => {
    addToCart(product, quantity);
    trackAddToCart(product, quantity);
    toast({
      title: 'تمت الإضافة للسلة',
      description: `${quantity} × ${product.name} تمت إضافته لسلة التسوق`,
    });
  };

  const incrementQuantity = () => {
    if (quantity < product.stock) {
      setQuantity(quantity + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${
              star <= rating
                ? 'fill-primary text-primary'
                : 'fill-muted text-muted'
            }`}
          />
        ))}
      </div>
    );
  };

  const openLightbox = (index: number) => {
    setLightboxImageIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    setLightboxImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setLightboxImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8" dir="rtl">
          <Link to={ROUTE_PATHS.HOME} className="hover:text-foreground transition-colors">
            الرئيسية
          </Link>
          <ChevronRight className="w-4 h-4 rotate-180" />
          <Link to={ROUTE_PATHS.PRODUCTS} className="hover:text-foreground transition-colors">
            المنتجات
          </Link>
          <ChevronRight className="w-4 h-4 rotate-180" />
          <span className="text-foreground font-medium">{product.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-12 mb-24" dir="rtl">
          {/* معرض الصور */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-4"
          >
            {/* الصورة الرئيسية */}
            <div 
              className="relative aspect-square rounded-2xl overflow-hidden bg-muted cursor-zoom-in group"
              onClick={() => openLightbox(selectedImageIndex)}
            >
              <img
                src={currentImage}
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
              {product.featured && (
                <Badge className="absolute top-4 right-4 bg-accent text-accent-foreground">
                  مميز
                </Badge>
              )}
              {product.stock < 10 && product.stock > 0 && (
                <Badge className="absolute top-4 left-4 bg-destructive text-destructive-foreground">
                  كمية محدودة
                </Badge>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                <Eye className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>

            {/* الصور المصغرة */}
            {allImages.length > 1 && (
              <div className="grid grid-cols-5 gap-2">
                {allImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImageIndex === index
                        ? 'border-primary scale-105'
                        : 'border-transparent hover:border-muted-foreground/50'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`صورة ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex flex-col"
          >
            <div className="mb-4">
              <Badge variant="secondary" className="mb-4">
                {product.category === 'baby' && 'مستلزمات البيبي'}
                {product.category === 'mother' && 'مستلزمات الأم'}
                {product.category === 'cosmetics' && 'مستحضرات التجميل'}
                {product.category === 'perfume' && 'العطور'}
              </Badge>
              <h1 className="text-4xl font-bold mb-2">{product.name}</h1>
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-2">
                  {renderStars(product.rating)}
                  <span className="text-sm text-muted-foreground">
                    ({product.reviewCount} تقييم)
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Eye className="w-4 h-4" />
                  <span>{product.views.toLocaleString('ar-EG')} مشاهدة</span>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-sm text-muted-foreground">الماركة:</span>
                <Badge variant="outline">{product.brand}</Badge>
              </div>
            </div>

            <div className="text-3xl font-bold text-primary mb-6">
              {formatPrice(product.price)}
            </div>

            <p className="text-muted-foreground mb-6 leading-relaxed">
              {product.description}
            </p>

            {product.colors && product.colors.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold mb-3">الألوان المتاحة:</h3>
                <div className="flex flex-wrap gap-3">
                  {product.colors.map((color, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedColorIndex(index)}
                      className={`px-4 py-2 rounded-lg border-2 transition-all ${
                        selectedColorIndex === index
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      {color.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold">الحالة:</span>
                {product.stock > 0 ? (
                  <Badge className="bg-chart-3 text-white">
                    متوفر ({product.stock} قطعة)
                  </Badge>
                ) : (
                  <Badge variant="destructive">نفذت الكمية</Badge>
                )}
              </div>
            </div>

            <Separator className="my-6" />

            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center border border-border rounded-lg">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={decrementQuantity}
                  disabled={quantity <= 1}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="px-6 py-2 font-semibold">{quantity}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={incrementQuantity}
                  disabled={quantity >= product.stock}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <Button
                size="lg"
                className="flex-1"
                onClick={handleAddToCart}
                disabled={product.stock === 0}
              >
                <ShoppingCart className="w-5 h-5 ml-2" />
                أضف للسلة
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-4 p-6 bg-muted rounded-lg">
              <div className="flex flex-col items-center text-center gap-2">
                <Truck className="w-6 h-6 text-primary" />
                <span className="text-sm font-medium">شحن مجاني</span>
                <span className="text-xs text-muted-foreground">للطلبات فوق 500 ج.م</span>
              </div>
              <div className="flex flex-col items-center text-center gap-2">
                <Shield className="w-6 h-6 text-primary" />
                <span className="text-sm font-medium">ضمان الجودة</span>
                <span className="text-xs text-muted-foreground">منتجات أصلية 100%</span>
              </div>
              <div className="flex flex-col items-center text-center gap-2">
                <RefreshCw className="w-6 h-6 text-primary" />
                <span className="text-sm font-medium">إرجاع سهل</span>
                <span className="text-xs text-muted-foreground">خلال 14 يوم</span>
              </div>
            </div>
          </motion.div>
        </div>

        {relatedProducts.length > 0 && (
          <div>
            <h2 className="text-3xl font-bold mb-8 text-right">منتجات مشابهة</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox للصور */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-7xl w-[95vw] h-[95vh] p-0 bg-black/95">
          <div className="relative w-full h-full flex items-center justify-center">
            {/* زر الإغلاق */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 z-50 text-white hover:bg-white/20"
              onClick={() => setLightboxOpen(false)}
            >
              <X className="w-6 h-6" />
            </Button>

            {/* زر السابق */}
            {allImages.length > 1 && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/20 h-12 w-12"
                onClick={prevImage}
              >
                <ChevronLeft className="w-8 h-8" />
              </Button>
            )}

            {/* الصورة */}
            <AnimatePresence mode="wait">
              <motion.img
                key={lightboxImageIndex}
                src={allImages[lightboxImageIndex]}
                alt={`صورة ${lightboxImageIndex + 1}`}
                className="max-w-full max-h-full object-contain"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
              />
            </AnimatePresence>

            {/* زر التالي */}
            {allImages.length > 1 && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/20 h-12 w-12"
                onClick={nextImage}
              >
                <ChevronRight className="w-8 h-8" />
              </Button>
            )}

            {/* عداد الصور */}
            {allImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm">
                {lightboxImageIndex + 1} / {allImages.length}
              </div>
            )}

            {/* الصور المصغرة */}
            {allImages.length > 1 && (
              <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto pb-2">
                {allImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setLightboxImageIndex(index)}
                    className={`flex-shrink-0 w-16 h-16 rounded border-2 overflow-hidden transition-all ${
                      lightboxImageIndex === index
                        ? 'border-white scale-110'
                        : 'border-transparent opacity-50 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`صورة ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}